"use client";

import axios from "axios";
import Link from "next/link";
import { useState } from "react";

interface FooterLink {
  href: string;
  text: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  className?: string;
}

interface FooterSectionProps {
  title: string;
  links: FooterLink[];
}

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (message) {
      setMessage("");
      setMessageType("");
    }
  };

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setMessage("Please enter your email address.");
      setMessageType("error");
      return;
    }

    if (!validateEmail(email)) {
      setMessage("Please enter a valid email address.");
      setMessageType("error");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post("/api/newsletter", { email });
      setMessage("Thank you for subscribing!");
      setMessageType("success");
      setEmail("");
    } catch (error: any) {
      console.error("Error submitting form:", error);
      if (error.response) {
        if (error.response.status === 409) {
          setMessage("This email is already subscribed.");
        } else {
          setMessage("Failed to process your request.");
        }
      } else {
        setMessage("Could not process your request.");
      }
      setMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 border-t border-gray-700 text-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-4 gap-8 md:text-sm text-xs text-white">
        {/* SEOZast Description */}
        <FooterSection
          title="SEOZast"
          links={[
            {
              href: "/#",
              text: "SEOZast: Affordable SEO and AI search optimization for everyone.",
              className: "font-normal",
            },
          ]}
        />

        {/* Links */}
        <FooterSection
          title="Links"
          links={[
            { href: "/#pricing", text: "Pricing" },
            { href: "/#demo", text: "Demo" },
            {
              href: "/seo-analyzer",
              text: "Seo Analyzer",
            },
            {
              href: "/aisearch-analyzer",
              text: "Ai Search Analyzer",
            },
            {
              href: "/keyword-generator",
              text: "Keywords Generator",
            },
            {
              href: "/directory",
              text: "Directory",
            },
            { href: "/affiliates", text: "Become an affiliate (50%)" },
          ]}
        />

        {/* Info */}
        <FooterSection
          title="Info"
          links={[
            { href: "/#faqs", text: "FAQs" },
            { href: "/privacy-policy", text: "Privacy Policy" },
            { href: "/terms-of-service", text: "Terms of Service" },
            { href: "/articles/how-to-publish", text: "How to publish" },
            { href: "/articles", text: "SEO guides" },
          ]}
        />

        {/* Contact */}
        <FooterSection
          title="Contact"
          links={[
            {
              href: "mailto:seozast@hotmail.com",
              text: "Contact us",
              onClick: (e) => {
                e.preventDefault();
                window.open("mailto:seozast@hotmail.com", "_blank", "noopener,noreferrer");
              },
            },
            {
              href: "https://twitter.com/eliana_jordan",
              text: "@eliana_jordan",
              className: "flex items-center",
              onClick: (e) => {
                e.preventDefault();
                window.open("https://twitter.com/eliana_jordan", "_blank", "noopener,noreferrer");
              },
            },
          ]}
        />

        {/* Newsletter Subscription (comentado como pediste) */}
        {/* <div>
          <h3 className="font-semibold sm:mb-3 mb-2 text-sm">Subscribe to our newsletter</h3>
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="flex">
              <input
                type="email"
                placeholder="Your email"
                className="flex-grow px-2 py-1 text-xs border border-gray-300 rounded-l focus:outline-none"
                value={email}
                onChange={handleEmailChange}
                disabled={isSubmitting}
              />
              <button
                type="submit"
                className={`px-2 py-1 bg-blue-600 text-white text-xs rounded-r hover:bg-blue-700 ${isSubmitting ? "opacity-70" : ""}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "..." : "Join"}
              </button>
            </div>
            {message && (
              <p className={`mt-1 text-xs ${messageType === "success" ? "text-green-600" : "text-red-600"}`}>
                {message}
              </p>
            )}
          </form>
        </div> */}
      </div>

      <div className="text-center sm:mt-6 mt-4 text-gray-200 mb-5">
        © {currentYear} SEOZast. All rights reserved.
      </div>
    </footer>
  );
};

// Componente FooterSection mejorado
const FooterSection: React.FC<FooterSectionProps> = ({ title, links }) => (
  <div>
    <h3 className="font-semibold sm:mb-3 mb-2 text-base">{title}</h3>
    <ul>
      {links.map((link, index) => (
        <li key={index} className="mb-2">
          {link.onClick ? (
            // Enlaces externos con onClick → <a> + preventDefault
            <a
              href={link.href}
              onClick={link.onClick}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-sm hover:underline text-white hover:text-emerald-400 ${link.className || ""}`}
            >
              {link.text === "@eliana_jordan" ? (
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  {link.text}
                </span>
              ) : (
                link.text
              )}
            </a>
          ) : (
            // Enlaces internos → usar <Link> de Next.js
            <Link
              href={link.href}
              className={`text-sm hover:underline text-white hover:text-emerald-400 ${link.className || ""}`}
            >
              {link.text}
            </Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default Footer;


// "use client";

// import axios from "axios";
// import Link from "next/link";
// import { useState } from "react";

// interface FooterLink {
//   href: string;
//   text: string;
//   onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
//   className?: string;
// }

// interface FooterSectionProps {
//   title: string;
//   links: FooterLink[];
// }

// const Footer = () => {
//   const currentYear = new Date().getFullYear();
//   const [email, setEmail] = useState("");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [message, setMessage] = useState("");
//   const [messageType, setMessageType] = useState(""); // 'success' or 'error'

//   const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     setEmail(e.target.value);
//     if (message) {
//       setMessage("");
//       setMessageType("");
//     }
//   };

//   const validateEmail = (email: string) => {
//     const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     return regex.test(email);
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();

//     if (!email) {
//       setMessage("Please enter your email address.");
//       setMessageType("error");
//       return;
//     }

//     if (!validateEmail(email)) {
//       setMessage("Please enter a valid email address.");
//       setMessageType("error");
//       return;
//     }

//     setIsSubmitting(true);

//     try {
//       const response = await axios.post("/api/newsletter", { email });
//       setMessage("Thank you for subscribing!");
//       setMessageType("success");
//       setEmail("");
//     } catch (error: any) {
//       console.error("Error submitting form:", error);
//       if (error.response) {
//         if (error.response.status === 409) {
//           setMessage("This email is already subscribed.");
//         } else {
//           setMessage("Failed to process your request.");
//         }
//       } else {
//         setMessage("Could not process your request.");
//       }
//       setMessageType("error");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   return (
//     <footer className="bg-gray-900 border-t border-gray-700 text-xs">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-4 gap-8 md:text-sm text-xs text-white">
//         {/* <div className="container mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 sm:gap-4 gap-2 md:text-sm text-xs text-gray-600"> */}
//         {/* SEOZast Description */}
//         <FooterSection
//           title="SEOZast"
//           links={[
//             {
//               href: "/#",
//               text: "SEOZast: Affordable SEO and AI search optimization for everyone.",
//               // text: "SEOZast: Automate SEO with AI-powered keyword research and content generation. Built for developers",
//               className: "font-normal",
//             },
//           ]}
//         />

//         {/* Links */}
//         <FooterSection
//           title="Links"
//           links={[
//             { href: "/#pricing", text: "Pricing" },
//             { href: "/#demo", text: "Demo" },
//             { href: "/articles/how-to-publish", text: "How to publish" },
//             { href: "/articles", text: "SEO guides" },
//           ]}
//         />

//         {/* Info */}
//         <FooterSection
//           title="Info"
//           links={[
//             { href: "/#faqs", text: "FAQs" },
//             { href: "/privacy-policy", text: "Privacy Policy" },
//             { href: "/terms-of-service", text: "Terms of Service" },
//             // { href: "/user-data-deletion", text: "User Data Deletion" },
//           ]}
//         />

//         {/* Contact */}
//         <FooterSection
//           title="Contact"
//           links={[
//             {
//               href: "mailto:seozast@hotmail.com",
//               text: "Contact us",
//             },
//             {
//               href: "https://twitter.com/eliana_jordan",
//               text: "@eliana_jordan",
//               className: "flex items-center",
//               onClick: (e) => {
//                 window.open("https://twitter.com/eliana_jordan", "_blank", "noopener,noreferrer");
//               },
//             },
//           ]}
//         />

//         {/* Newsletter Subscription */}
//         {/* <div>
//           <h3 className="font-semibold sm:mb-3 mb-2 text-sm">Subscribe to our newsletter</h3>
//           <form onSubmit={handleSubmit} className="flex flex-col">
//             <div className="flex">
//               <input
//                 type="email"
//                 placeholder="Your email"
//                 className="flex-grow px-2 py-1 text-xs border border-gray-300 rounded-l focus:outline-none"
//                 value={email}
//                 onChange={handleEmailChange}
//                 disabled={isSubmitting}
//               />
//               <button
//                 type="submit"
//                 className={`px-2 py-1 bg-blue-600 text-white text-xs rounded-r hover:bg-blue-700 ${isSubmitting ? "opacity-70" : ""}`}
//                 disabled={isSubmitting}
//               >
//                 {isSubmitting ? "..." : "Join"}
//               </button>
//             </div>
//             {message && (
//               <p className={`mt-1 text-xs ${messageType === "success" ? "text-green-600" : "text-red-600"}`}>
//                 {message}
//               </p>
//             )}
//           </form>
//         </div> */}
//       </div>

//       <div className="text-center sm:mt-6 mt-4 text-gray-200 mb-5">
//         © {currentYear} SEOZast. All rights reserved.
//       </div>
//     </footer>
//   );
// };

// const FooterSection: React.FC<FooterSectionProps> = ({ title, links }) => (
//   <div>
//     <h3 className="font-semibold sm:mb-3 mb-2 text-base">{title}</h3>
//     <ul>
//       {links.map((link, index) => (
//         <li key={index} className="mb-2">
//           {link.onClick ? (
//             <a
//               href={link.href}
//               onClick={link.onClick}
//               className={`text-sm hover:underline ${link.className || ""}`}
//             >
//               {link.text === "@eliana_jordan" ? (
//                 <span className="flex items-center text-white hover:text-emerald-400">
//                   <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
//                     <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
//                   </svg>
//                   {link.text}
//                 </span>
//               ) : (
//                 link.text
//               )}
//             </a>
//           ) : (
//             <Link href={link.href} className={`text-sm hover:underline text-white hover:text-emerald-400 ${link.className || ""}`}>
//               {link.text}
//             </Link>
//           )}
//         </li>
//       ))}
//     </ul>
//   </div>
// );

// export default Footer;
